import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { apartmentStore } from '@stores/apartmentStore';
import { contractStore } from '@stores/contractStore';
import { userStore } from '@stores/userStore';

import { IConditionalWrapper, ParsedEntry } from '../../types';
import { ResolveEntry } from '../ResolveEntry';

import {
  isAsyncCondition,
  isValidConditions,
  isValidConditionsAsync,
  SyncCondition,
} from './ConditionalWrapper.utils';

/** Wrapper that renders content conditionally depending on conditions set in Contentful */
export const ConditionalWrapper = observer(
  ({
    conditions,
    content,
    fallbackContent,
    negate,
  }: ParsedEntry<IConditionalWrapper>) => {
    const hasAsyncConditions = conditions.some(isAsyncCondition);

    const [isConditionValid, setIsConditionValid] = useState(
      hasAsyncConditions
        ? false
        : isValidConditions(
            { conditions: conditions as SyncCondition[], negate },
            { contractStore, apartmentStore, userStore }
          )
    );

    const [isLoading, setIsLoading] = useState(hasAsyncConditions);

    useEffect(() => {
      // Ignore results of a stale check that resolves after a newer one has started
      let isStale = false;

      isValidConditionsAsync(
        { conditions, negate },
        { contractStore, apartmentStore, userStore }
      )
        .then((isValid) => {
          if (!isStale) setIsConditionValid(isValid);
        })
        .finally(() => {
          if (!isStale) setIsLoading(false);
        });

      return () => {
        isStale = true;
      };

      // Check conditions on first render and when contract, apartment or user is changed
    }, [
      apartmentStore.selectedApartment?.apartmentId,
      contractStore.selectedContract?.contractId,
      userStore.user?.crmID,
    ]);

    // only if all conditions are met, show desired content
    if (isConditionValid) {
      return content.map((entry) => (
        <ResolveEntry key={entry.id} entry={entry} />
      ));
    }

    if (!fallbackContent || isLoading) return null;

    return fallbackContent.map((entry) => (
      <ResolveEntry key={entry.id} entry={entry} />
    ));
  }
);
